<script setup lang="ts">
import { FormProgressStepList } from "@/constants/form";
import SingleSelect from "~/components/form/SingleSelect.vue";
import {
  purposeList,
  workList,
  wishList,
  inflowSources,
} from "~/constants/form";

definePageMeta({
  layout: "no-bread-crumbs",
});

// インスタグラムからのアクセス(queryを入れる)
const isInstagram = computed(() => {
  return useRoute().query.instagram;
});

const { isAgree, inquiryData, surveyData } = useInquiry();

// 変更を監視して意図しない離脱防止を起動する
const { isChange } = useLeaveForms();

// 確認画面から戻ってきたなどに編集中にする
onMounted(() => {
  if (isAgree.value) {
    isChange.value = true;
  }
});

// リクエストヘッダーのリファラーがあれば保存する
const header = useRequestHeaders();
if (header.referer) {
  inquiryData.value.referer = header.referer;
}

watchOnce(
  () => inquiryData.value,
  () => {
    isChange.value = true;
  },
  { deep: true },
);

// 施工例・レビュー・プランから問い合わせに対応
const sekouCd = computed(() => useRoute().query.sekou_cd);
const koujiCd = computed(() => useRoute().query.kouji_cd);
const planCd = computed(() => useRoute().query.plan_cd);

inquiryData.value.sekou_cd = Number(sekouCd.value) || 0;

inquiryData.value.kouji_cd = Number(koujiCd.value) || 0;

inquiryData.value.plan_cd = Number(planCd.value) || 0;

function handleSubmit(e: Event) {
  isChange.value = false;
  // sesstionStorageに上書き保存
  useSessionStorage("inquiry-temp", inquiryData.value, {
    mergeDefaults: (value, initial) => {
      return { ...initial };
    },
  });
  useSessionStorage("survey-temp", surveyData.value, {
    mergeDefaults: (value, initial) => {
      return { ...initial };
    },
  });
  useSessionStorage("is-agree", isAgree.value, {
    mergeDefaults: (value, initial) => {
      return initial;
    },
  });

  useRouter().push("/inquiry/confirm");
}

useHead({
  title:
    "資料請求・プラン作成（無料！）外構・リフォーム・エクステリア工事のお問い合わせ",
  meta: [
    {
      name: "description",
      content:
        "外構（エクステリア）費用、プラン作成、何でも気軽にお問い合わせください。庭、ガレージ、アプローチ、お客様本位のファミリー庭園が快適空間作りのお手伝いをいたします。",
    },
    {
      name: "robots",
      content: "noindex,nofollow",
    },
  ],
});
</script>

<template>
  <div class="p-inquiry mb-16">
    <template v-if="isInstagram">
      <div class="bg-gradient-to-b from-[#e0f4eb] to-white">
        <h2 class="text-xl py-3 text-center font-bold">
          <Icon
            class="text-famitei-green"
            size="35px"
            name="mdi:arrow-down-bold"
          />
          施工例やプラン事例はこちらから
        </h2>
        <!-- instagram用 -->
        <SlideTopRecommend mode="instagram" />

        <hr class="my-8 border-2" />

        <p class="text-xl py-1 text-center font-bold">
          <Icon
            class="text-famitei-green"
            size="35px"
            name="mdi:arrow-down-bold"
          />
          お問合せはこちらから
        </p>
        <p class="text-xl py-1 text-center font-bold">
          現地調査・資料請求・お見積を
          <span class="text-[#e12323]">無料</span> で依頼
        </p>
      </div>
    </template>
    <h2
      v-else
      class="p-inquiry__header text-lg text-center font-bold bg-[#dae3c2] py-4 tablet:text-2xl mb-8"
    >
      現地調査・資料請求・お見積を
      <span class="text-xl text-[#e12323] font-bold tablet:text-3xl">無料</span>
      で依頼！
    </h2>

    <NewsNotice class="wrapper mb-8" />

    <div class="wrapper">
      <ListProgressStep class="mb-4" :flow="FormProgressStepList" current="1" />
      <FormParent class="mb-8" :submit="handleSubmit">
        <!-- 必須項目 -->
        <div class="p-inquiry__main">
          <div class="mb-8">
            <p class="p-inquiry__title">
              ■外構・庭・ガレージ・アプローチ・水まわりの工事・リフォームなどお気軽にお問い合わせ下さい。
            </p>
            <p class="text-red-600 mb-4">
              ※
              水・木はお休みをいただいておりますため、お返事は金曜日以降となります。
            </p>
          </div>

          <!-- ご希望内容 -->
          <div class="mb-8">
            <p class="p-inquiry__label p-inquiry__label--required mb-2">
              ご希望内容
            </p>
            <div class="p-inquiry__area">
              <FormRadioSelect
                class="grid grid-cols-1 tablet:grid-cols-3 gap-2"
                :required="true"
                v-model="inquiryData.purpose"
                :list="purposeList"
                name="purpose"
              />
            </div>
          </div>
          <!-- ご予定工種 -->
          <div class="mb-8">
            <p class="p-inquiry__label p-inquiry__label--required mb-2">
              ご予定工種
            </p>
            <div class="p-inquiry__area">
              <FormRadioSelect
                class="grid grid-cols-1 tablet:grid-cols-3 gap-2"
                :required="true"
                v-model="inquiryData.works"
                :list="workList"
                name="works"
              />
            </div>
          </div>
          <!-- お名前 -->
          <div class="mb-8">
            <p class="p-inquiry__label p-inquiry__label--required mb-2">
              お名前
            </p>
            <div class="p-inquiry__area">
              <FormAutokanaInput
                :required="true"
                :required_kana="true"
                v-model:name="inquiryData.fullname"
                v-model:kana="inquiryData.furigana"
              />
            </div>
          </div>

          <!-- 工事現場のご住所 -->
          <div class="mb-8">
            <p class="p-inquiry__label p-inquiry__label--required mb-2">
              工事予定のご住所
            </p>
            <div class="p-inquiry__area">
              <FormAddressNumberInput
                :required="true"
                v-model:postnum="inquiryData.post2"
                v-model:address="inquiryData.address2"
                v-model:address_number="inquiryData.address2_number"
              />
              <label class="py-2 flex items-center">
                <input
                  type="checkbox"
                  class="mr-3"
                  v-model="inquiryData.is_different_address"
                />
                <strong>現在、お住まいのご住所が違う場合はこちら</strong>
              </label>
            </div>
          </div>

          <!-- ご住所 -->
          <div class="mb-8" v-if="inquiryData.is_different_address">
            <p class="p-inquiry__label p-inquiry__label--required mb-2">
              お住まいのご住所
            </p>
            <div class="p-inquiry__area">
              <FormAddressNumberInput
                :required="inquiryData.is_different_address"
                v-model:postnum="inquiryData.post"
                v-model:address="inquiryData.address"
                v-model:address_number="inquiryData.address_number"
              />
            </div>
          </div>

          <!-- 電話番号 -->
          <div class="mb-8">
            <p class="p-inquiry__label p-inquiry__label--required mb-2">
              電話番号
            </p>
            <div class="p-inquiry__area">
              <FormTelephoneInput
                :required="true"
                label2="例:00011112222"
                v-model="inquiryData.tel"
              />
            </div>
          </div>

          <!-- Eメール -->
          <div class="mb-8">
            <p class="p-inquiry__label p-inquiry__label--required mb-2">
              Eメール
            </p>
            <div class="p-inquiry__area">
              <FormMailInput
                :required="true"
                v-model="inquiryData.email"
                label2="例:example@example.com"
              />
            </div>
          </div>
          <div class="mb-8">
            <p class="p-inquiry__label p-inquiry__label--required mb-4">
              当社HPをどこで知りましたか？
            </p>
            <div class="p-inquiry__area">
              <SingleSelect
                v-model="surveyData.q2"
                label="ファミテイを知ったきっかけ"
                :list="inflowSources"
                :required="true"
              />
            </div>
          </div>
          <!-- お問い合わせ内容 -->
          <div class="mb-8">
            <label
              for="textarea"
              class="p-inquiry__label p-inquiry__label--optional mb-2"
            >
              お問い合わせ内容
            </label>
            <div class="p-inquiry__area">
              <textarea
                id="textarea"
                class="p-inquiry__textarea m-2"
                v-model="inquiryData.message"
              ></textarea>
            </div>
          </div>
        </div>
        <!-- 補助項目 -->
        <div class="p-inquiry__sub">
          <p class="p-inquiry__title mb-4">
            ■打合せを円滑に進める為に下記内容を可能な範囲でご記入ください。
          </p>
          <div>
            <!-- 竣工時期 -->
            <div class="mb-8">
              <label
                for="completion"
                class="p-inquiry__label p-inquiry__label--optional mb-2"
              >
                竣工時期
              </label>
              <div class="p-inquiry__area">
                <FormDateInput
                  v-model="inquiryData.completion"
                  id="completion"
                  label=""
                  label2="例:2000-01-01"
                  placeholder="竣工時期を入力(ハイフンは自動)"
                />
              </div>
            </div>
            <!-- ハウスメーカー -->
            <div class="mb-8">
              <p class="p-inquiry__label p-inquiry__label--optional mb-2">
                ハウスメーカー
              </p>
              <div class="p-inquiry__area">
                <FormInput
                  v-model="inquiryData.housemaker"
                  id="housemaker"
                  placeholder="ハウスメーカー名を入力"
                  label="ハウスメーカー"
                  alert="ハウスメーカーをご入力ください"
                />
              </div>
            </div>
          </div>
        </div>
        <p class="p-inquiry__title mb-4">
          ■下記アンケートにご協力お願いします。
        </p>
        <!-- アンケート -->
        <MenuAccordionPreview>
          <p class="p-inquiry__label p-inquiry__label--multiple mb-4">
            Q1.工事完了後にやりたいこと
          </p>
          <FormMultipleSelect
            class="mb-4"
            :list="wishList"
            v-model="surveyData.q1"
          />
        </MenuAccordionPreview>

        <!-- 同意確認 -->
        <FormAgreement :require="true" v-model="isAgree">
          <NuxtLink
            to="/contents.php/privacy"
            target="_blank"
            class="underline mb-4"
            >【個人情報の取扱いについて】にご同意の上、お申込みください</NuxtLink
          >
        </FormAgreement>
        <FormActionRow :disabled="!isAgree" submit_text="確認画面に進む" />
      </FormParent>
    </div>
  </div>
</template>
<style scoped lang="scss">
.p-inquiry {
  &__title {
    @apply text-base font-bold border-b tablet:text-xl;
  }
  &__area {
    @apply bg-gray-100 rounded-lg p-2;
  }
  &__label {
    @apply flex items-center font-bold whitespace-nowrap tablet:text-xl;
    &--required {
      @apply flex items-center whitespace-nowrap;
      &:after {
        @apply bg-famitei-green text-white rounded-md text-xs px-1 py-0.5 ml-2;
        content: "必須";
      }
    }
    &--optional {
      @apply flex items-center whitespace-nowrap;
      &:after {
        @apply bg-gray-500 text-white rounded-md text-xs px-1 py-0.5 ml-2;
        content: "任意";
      }
    }
    &--possible {
      @apply flex items-center whitespace-nowrap;
      &:after {
        @apply bg-gray-500 text-white rounded-md text-xs px-1 py-0.5 ml-2;
        content: "分かる部分までご記入ください";
      }
    }
    &--multiple {
      @apply flex items-center whitespace-nowrap;
      &:after {
        @apply bg-gray-500 text-white rounded-md text-xs px-1 py-0.5 ml-2;
        content: "複数選択可";
      }
    }
  }
  &__textarea {
    @apply w-[calc(100%-1.0rem)] h-32 border border-gray-200;
  }
}
</style>
